import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, Navigate } from 'react-router';
import { useAuth } from '@/lib/use-auth';
import { ApiError } from '@/lib/api';
import { fetchMyTutorProfile } from '@/lib/tutor-api';
import {
  Calendar,
  Clock,
  Video,
  Users,
  ChevronRight,
  Hourglass,
  Coins,
  ShieldAlert,
  CalendarDays,
  CheckCircle,
  X,
  ArrowUpRight,
} from 'lucide-react';
import { getMyBookings, acceptBooking, declineBooking } from '@/lib/booking-api';
import { getMyEarnings } from '@/lib/earning-api';
import type { TutorProfileResponse } from '@/lib/schemas/tutor-profile';
import type { BookingResponse } from '@/lib/schemas/booking';
import type { TutorEarningResponse } from '@/lib/schemas/earning';
import { Skeleton } from '@/components/ui/skeleton';
import { StatCard, formatClassDate, formatTime } from './shared';

// ─── Sub-Components ───────────────────────────────────────────────────────────

interface TutorBookingDashboardCardProps {
  booking: BookingResponse;
  onUpdate: (updated: BookingResponse) => void;
}

function TutorBookingDashboardCard({ booking: b, onUpdate }: TutorBookingDashboardCardProps) {
  const { accessToken } = useAuth();
  const [actionError, setActionError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [acceptOpen, setAcceptOpen] = useState(false);
  const [videoLink, setVideoLink] = useState('');
  const [declineOpen, setDeclineOpen] = useState(false);
  const [tutorNotes, setTutorNotes] = useState('');

  const startTime = new Date(b.slotStartUtc);
  const endTime = new Date(b.slotEndUtc);

  async function handleAccept() {
    if (!accessToken || !videoLink.trim()) return;
    setBusy(true);
    setActionError(null);
    try {
      const updated = await acceptBooking(accessToken, b.id, { videoLink: videoLink.trim() });
      onUpdate(updated);
      setAcceptOpen(false);
      setVideoLink('');
    } catch (e) {
      setActionError(e instanceof ApiError ? e.message : 'Failed to accept booking');
    } finally {
      setBusy(false);
    }
  }

  async function handleDecline() {
    if (!accessToken) return;
    setBusy(true);
    setActionError(null);
    try {
      const updated = await declineBooking(accessToken, b.id, {
        tutorNotes: tutorNotes.trim() || undefined,
      });
      onUpdate(updated);
      setDeclineOpen(false);
      setTutorNotes('');
    } catch (e) {
      setActionError(e instanceof ApiError ? e.message : 'Failed to decline booking');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="relative overflow-hidden rounded-2xl border border-slate-200/80 bg-white/70 p-5 shadow-sm backdrop-blur-md transition-all duration-300 hover:-translate-y-0.5 hover:shadow-md dark:border-slate-800/80 dark:bg-slate-800/75 dark:hover:bg-slate-800/90 dark:hover:border-slate-700">
      <div className="absolute inset-y-0 left-0 w-1.5 bg-gradient-to-b from-indigo-500 to-blue-600" />
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 pl-3">
        <div className="space-y-2 min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`inline-flex rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider ${
              b.type === 'demo'
                ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300'
                : 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300'
            }`}>
              {b.type === 'demo' ? 'Demo Class' : 'Paid Class'}
            </span>
            <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold ${
              b.status === 'accepted'
                ? 'bg-emerald-50 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400'
                : b.status === 'completed'
                ? 'bg-blue-50 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400'
                : 'bg-amber-50 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400'
            }`}>
              <span className={`h-1.5 w-1.5 rounded-full ${
                b.status === 'accepted' ? 'bg-emerald-500 animate-pulse' : b.status === 'completed' ? 'bg-blue-500' : 'bg-amber-500'
              }`} />
              {b.status === 'accepted' ? 'Confirmed' : b.status === 'completed' ? 'Completed' : 'Pending Request'}
            </span>
          </div>

          <p className="text-base font-bold text-slate-800 dark:text-slate-100 truncate">
            {b.classTitle || b.subject || 'Interactive Session'}
          </p>

          <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs text-slate-500 dark:text-slate-400 font-semibold">
            <span className="flex items-center gap-1">
              <Calendar className="h-3.5 w-3.5 text-blue-500" />
              {formatClassDate(startTime)}
            </span>
            <span className="flex items-center gap-1">
              <Clock className="h-3.5 w-3.5 text-violet-500" />
              {formatTime(startTime)} – {formatTime(endTime)}
            </span>
            {b.parentNotes && (
              <span className="truncate max-w-[300px] italic font-medium text-slate-500 dark:text-slate-400">
                Note: "{b.parentNotes}"
              </span>
            )}
          </div>
          
          {b.videoLink && b.status === 'accepted' && (
            <div className="mt-2 text-xs font-bold text-indigo-600 dark:text-indigo-400 truncate">
              Meeting URL:{' '}
              <a href={b.videoLink} target="_blank" rel="noopener noreferrer" className="hover:underline">
                {b.videoLink}
              </a>
            </div>
          )}
        </div>

        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 shrink-0">
          {/* Join button for accepted booking with video link */}
          {b.status === 'accepted' && b.videoLink && (
            <a
              href={b.videoLink}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-1.5 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 px-4.5 py-2.5 text-xs font-bold text-white shadow-md hover:from-blue-750 hover:to-indigo-750 hover:scale-[1.02] active:scale-95 duration-200"
            >
              <Video className="h-3.5 w-3.5" />
              <span>Join Class</span>
            </a>
          )}

          {/* Pending state options */}
          {b.status === 'pending' && (
            <div className="space-y-2 w-full">
              {!acceptOpen && !declineOpen && (
                <div className="flex gap-2 justify-end">
                  <button
                    onClick={() => setAcceptOpen(true)}
                    className="rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white px-3.5 py-2.5 text-xs font-bold shadow-sm transition-all hover:scale-[1.02] cursor-pointer"
                  >
                    Accept Invite
                  </button>
                  <button
                    onClick={() => setDeclineOpen(true)}
                    className="rounded-xl border border-red-200 text-red-650 hover:bg-red-50 dark:border-red-900/50 dark:text-red-400 dark:hover:bg-red-950/30 px-3.5 py-2.5 text-xs font-bold transition-all cursor-pointer"
                  >
                    Decline
                  </button>
                </div>
              )}

              {acceptOpen && (
                <div className="space-y-2 p-1.5 bg-slate-50 dark:bg-slate-800/50 rounded-xl border border-slate-100 dark:border-slate-800 min-w-[220px]">
                  <input
                    type="url"
                    value={videoLink}
                    onChange={(e) => setVideoLink(e.target.value)}
                    placeholder="Enter Zoom / Meet link..."
                    className="w-full rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-emerald-400 dark:border-slate-700 dark:bg-slate-800 dark:text-white"
                  />
                  <div className="flex gap-1.5 justify-end">
                    <button
                      onClick={() => void handleAccept()}
                      disabled={busy || !videoLink.trim()}
                      className="rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white px-2.5 py-1 text-xs font-bold disabled:opacity-50 cursor-pointer"
                    >
                      {busy ? 'Saving...' : 'Confirm'}
                    </button>
                    <button
                      onClick={() => { setAcceptOpen(false); setVideoLink(''); }}
                      className="rounded-lg bg-slate-200 hover:bg-slate-300 dark:bg-slate-700 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-200 px-2.5 py-1 text-xs font-bold cursor-pointer"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              {declineOpen && (
                <div className="space-y-2 p-1.5 bg-slate-50 dark:bg-slate-800/50 rounded-xl border border-slate-100 dark:border-slate-800 min-w-[220px]">
                  <textarea
                    value={tutorNotes}
                    onChange={(e) => setTutorNotes(e.target.value)}
                    placeholder="Decline reason (optional)..."
                    rows={2}
                    className="w-full rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-red-400 dark:border-slate-700 dark:bg-slate-800 dark:text-white"
                  />
                  <div className="flex gap-1.5 justify-end">
                    <button
                      onClick={() => void handleDecline()}
                      disabled={busy}
                      className="rounded-lg bg-red-650 hover:bg-red-700 text-white px-2.5 py-1 text-xs font-bold disabled:opacity-50 cursor-pointer"
                    >
                      {busy ? 'Saving...' : 'Decline Invite'}
                    </button>
                    <button
                      onClick={() => { setDeclineOpen(false); setTutorNotes(''); }}
                      className="rounded-lg bg-slate-200 hover:bg-slate-300 dark:bg-slate-700 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-200 px-2.5 py-1 text-xs font-bold cursor-pointer"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      {actionError && (
        <p className="mt-2 text-xs text-red-600 font-bold text-right pl-3">{actionError}</p>
      )}
    </div>
  );
}

function TutorDashboardSkeleton() {
  return (
    <div className="w-full space-y-8 py-2">
      {/* Stats Overview Skeleton */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 items-stretch">
        {[...Array(4)].map((_, i) => (
          <div key={i} className="h-full rounded-lg border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-700 dark:bg-slate-800 flex items-center justify-between gap-3">
            <div className="space-y-2 flex-1 min-w-0">
              <Skeleton className="h-3.5 w-16 bg-slate-200 dark:bg-slate-700 rounded-full" />
              <Skeleton className="h-8 w-10 bg-slate-200 dark:bg-slate-700 rounded-lg" />
            </div>
            <Skeleton className="h-10 w-10 bg-slate-200 dark:bg-slate-700 rounded-lg shrink-0" />
          </div>
        ))}
      </div>

      {/* Grid: Upcoming Classes and Profile Overview */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-stretch">
        {/* Classes List Card */}
        <div className="lg:col-span-2 rounded-lg border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-700 dark:bg-slate-800 flex flex-col justify-between">
          <div className="space-y-4">
            {[...Array(2)].map((_, i) => (
              <div key={i} className="rounded-lg border border-slate-100 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-900/30 p-5 flex items-center gap-4">
                <div className="flex-1 space-y-2">
                  <div className="flex gap-2">
                    <Skeleton className="h-5 w-16 bg-slate-200 dark:bg-slate-700 rounded-full" />
                    <Skeleton className="h-5 w-16 bg-slate-200 dark:bg-slate-700 rounded-full" />
                  </div>
                  <Skeleton className="h-6 w-1/3 bg-slate-200 dark:bg-slate-700 rounded-lg" />
                  <Skeleton className="h-4 w-1/2 bg-slate-200 dark:bg-slate-700 rounded-lg" />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Profile Card & Earnings Summary */}
        <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-700 dark:bg-slate-800 space-y-4 flex flex-col justify-between">
          <div className="flex items-center gap-3">
            <Skeleton className="h-12 w-12 bg-slate-200 dark:bg-slate-700 rounded-lg shrink-0" />
            <div className="space-y-2 flex-1">
              <Skeleton className="h-4.5 w-24 bg-slate-200 dark:bg-slate-700 rounded-lg" />
              <Skeleton className="h-3.5 w-32 bg-slate-200 dark:bg-slate-700 rounded-lg" />
            </div>
          </div>
          <div className="space-y-3 pt-2">
            <Skeleton className="h-2 w-full bg-slate-200 dark:bg-slate-700 rounded-full" />
            <Skeleton className="h-4 w-1/2 bg-slate-200 dark:bg-slate-700 rounded-lg" />
            <Skeleton className="h-4 w-1/3 bg-slate-200 dark:bg-slate-700 rounded-lg" />
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export function TutorDashboard() {
  const { user, accessToken } = useAuth();
  const [profile, setProfile] = useState<TutorProfileResponse | null | undefined>(undefined);
  const [bookings, setBookings] = useState<BookingResponse[]>([]);
  const [earnings, setEarnings] = useState<TutorEarningResponse[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loadingStats, setLoadingStats] = useState(false);

  const [hideLiveStatusBanner, setHideLiveStatusBanner] = useState<boolean>(() => {
    return sessionStorage.getItem('hideLiveStatusBanner') === 'true';
  });

  function closeLiveStatusBanner() {
    sessionStorage.setItem('hideLiveStatusBanner', 'true');
    setHideLiveStatusBanner(true);
  }

  useEffect(() => {
    if (!accessToken) return;
    let cancelled = false;
    (async () => {
      try {
        const p = await fetchMyTutorProfile(accessToken);
        if (!cancelled) setProfile(p);

        setLoadingStats(true);
        // Fetch bookings & earnings
        try {
          const b = await getMyBookings(accessToken);
          if (!cancelled) setBookings(b);
        } catch (err) {
          console.error('[TutorHome] Failed to load bookings', err);
        }

        try {
          const e = await getMyEarnings(accessToken);
          if (!cancelled) setEarnings(e);
        } catch (err) {
          console.error('[TutorHome] Failed to load earnings', err);
        }
        if (!cancelled) setLoadingStats(false);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof ApiError ? err.message : 'Failed to load tutor details');
          setLoadingStats(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [accessToken]);

  // Derived tutor statistics
  const {
    activeStudentsCount,
    completedCount,
    hoursCompleted,
    pendingBookings,
    upcomingClasses,
    totalEarnings,
  } = useMemo(() => {
    const activeStudents = new Set(bookings.filter((b) => b.status === 'accepted' || b.status === 'completed').map((b) => b.childId));
    const completed = bookings.filter((b) => b.status === 'completed');
    
    let totalMs = 0;
    for (const b of completed) {
      const start = new Date(b.slotStartUtc).getTime();
      const end = new Date(b.slotEndUtc).getTime();
      totalMs += (end - start);
    }
    const hours = Math.round((totalMs / (1000 * 60 * 60)) * 10) / 10;

    const pending = bookings.filter((b) => b.status === 'pending');
    
    const now = Date.now();
    const upcoming = bookings
      .filter((b) => b.status === 'accepted' && new Date(b.slotEndUtc).getTime() > now)
      .sort((a, b) => new Date(a.slotStartUtc).getTime() - new Date(b.slotStartUtc).getTime());

    const held = earnings.filter((e) => e.status === 'held').reduce((s, e) => s + e.netMinor, 0);
    const eligible = earnings.filter((e) => e.status === 'eligible').reduce((s, e) => s + e.netMinor, 0);
    const released = earnings.filter((e) => e.status === 'released').reduce((s, e) => s + e.netMinor, 0);

    return {
      activeStudentsCount: activeStudents.size,
      completedCount: completed.length,
      hoursCompleted: hours,
      pendingBookings: pending,
      upcomingClasses: upcoming,
      totalEarnings: held + eligible + released,
    };
  }, [bookings, earnings]);

  const handleBookingUpdate = (updated: BookingResponse) => {
    setBookings((prev) => prev.map((b) => (b.id === updated.id ? updated : b)));
  };

  const formatTutorCurrency = useCallback((minor: number, currency: string) => {
    const symbol = currency === 'USD' ? '$' : '₹';
    const amount = minor / 100;
    return `${symbol}${amount.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
  }, []);

  if (error && profile === undefined) {
    return (
      <div className="rounded-2xl border border-red-200 bg-red-50/70 p-4 text-sm font-semibold text-red-700 dark:border-red-950/50 dark:bg-red-950/20 backdrop-blur-md flex items-center gap-2 m-6">
        <ShieldAlert className="h-5 w-5 shrink-0 text-red-500" />
        <span>{error}</span>
      </div>
    );
  }

  if (profile === undefined || loadingStats) {
    return <TutorDashboardSkeleton />;
  }

  if (profile === null || profile.publishStatus === 'draft') {
    return <Navigate to="/onboarding" replace />;
  }

  const payoutCurrency = profile.payoutCurrency || 'INR';

  return (
    <div className="w-full space-y-8 py-2 animate-fade-in">
      {error && (
        <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm font-semibold text-red-700 dark:border-red-900/50 dark:bg-red-950/20 flex items-center gap-2">
          <ShieldAlert className="h-5 w-5 shrink-0 text-red-500" />
          <span>{error}</span>
        </div>
      )}

      {/* Warnings & Listing Status Banner */}
      {profile.publishStatus === 'changes_requested' && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-5 dark:border-red-900/50 dark:bg-red-950/20">
          <div className="flex gap-3">
            <ShieldAlert className="h-5.5 w-5.5 text-red-655 dark:text-red-400 shrink-0 mt-0.5" />
            <div className="space-y-1 flex-1">
              <h3 className="text-sm font-bold text-red-800 dark:text-red-300">
                Action Required: Profile Modifications Requested
              </h3>
              <p className="text-xs text-slate-600 dark:text-slate-400 leading-relaxed">
                The administrator has requested revisions before publishing your profile. Please review the feedback below and update your details.
              </p>
              {profile.changeRequestNote && (
                <div className="mt-3 rounded-lg bg-white dark:bg-slate-800 p-4 text-xs font-semibold text-slate-800 dark:text-slate-200 border border-red-100 dark:border-red-900/40">
                  {profile.changeRequestNote}
                </div>
              )}
              <div className="pt-2">
                <Link
                  to="/onboarding"
                  className="inline-flex items-center gap-1.5 rounded-md bg-red-600 hover:bg-red-700 text-white px-4 py-2 text-xs font-bold transition-colors cursor-pointer"
                >
                  Edit Profile Setup
                  <ChevronRight className="h-3.5 w-3.5" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}

      {profile.publishStatus === 'pending_review' && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-5 dark:border-amber-900/50 dark:bg-amber-900/20">
          <div className="flex gap-3">
            <Hourglass className="h-5.5 w-5.5 text-amber-655 dark:text-amber-400 shrink-0 mt-0.5" />
            <div className="space-y-1">
              <h3 className="text-sm font-bold text-amber-600 dark:text-amber-300">
                Your profile is in review
              </h3>
              <p className="text-xs text-slate-600 dark:text-slate-400 leading-relaxed">
                Your profile details have been submitted. Our compliance team is verifying your credentials, demo video, and bank authentication. This usually takes up to 24 business hours.
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 dark:bg-slate-900 px-2.5 py-1 text-[11px] font-bold text-slate-600 dark:text-slate-300">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                  Credentials Verified
                </span>
                <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 dark:bg-slate-900 px-2.5 py-1 text-[11px] font-bold text-slate-600 dark:text-slate-300">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                  Availability Configured
                </span>
                <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 dark:bg-slate-900 px-2.5 py-1 text-[11px] font-bold text-slate-600 dark:text-slate-300">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                  Payout Method Connected
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {profile.publishStatus === 'paused' && (
        <div className="rounded-lg border border-slate-200 bg-slate-50 p-5 dark:border-slate-800/80 dark:bg-slate-800/40">
          <div className="flex gap-3">
            <ShieldAlert className="h-5.5 w-5.5 text-slate-500 dark:text-slate-400 shrink-0 mt-0.5" />
            <div className="space-y-1">
              <h3 className="text-sm font-bold text-slate-800 dark:text-slate-300">
                Listing Paused
              </h3>
              <p className="text-xs text-slate-600 dark:text-slate-400 leading-relaxed">
                Your listings are currently paused. You will not show up in parent tutor searches. Contact admin support or check onboarding settings to make your profile live.
              </p>
            </div>
          </div>
        </div>
      )}

      {profile.publishStatus === 'published' && !hideLiveStatusBanner && (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 dark:border-emerald-900/50 dark:bg-emerald-900/20 flex items-start gap-2.5 relative">
          <div className="pt-0.5 shrink-0"><span className="flex h-3 w-3 rounded-full bg-emerald-500"></span></div>
          <div className="flex-1 pr-6">
            <p className="text-xs font-bold text-emerald-800 dark:text-emerald-300">
              Listing Status: Live & Active
            </p>
            <p className="text-[11px] text-slate-600 dark:text-slate-400 mt-0.5">
              Your profile is active in search results. Parents can book your available calendar slots directly.
            </p>
          </div>
          <button
            onClick={closeLiveStatusBanner}
            className="absolute top-4 right-4 text-emerald-600 hover:text-emerald-800 dark:text-emerald-400 dark:hover:text-emerald-200 cursor-pointer"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Tutor statistics grid */}
      {!loadingStats && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 items-stretch">
          <StatCard
            icon={<CheckCircle className="h-5 w-5" />}
            label="Classes Taught"
            value={completedCount}
            textAccent="text-slate-900 dark:text-white"
          />
          <StatCard
            icon={<Clock className="h-5 w-5" />}
            label="Hours Completed"
            value={`${hoursCompleted} hrs`}
            textAccent="text-slate-900 dark:text-white"
          />
          <StatCard
            icon={<Users className="h-5 w-5" />}
            label="Active Students"
            value={activeStudentsCount}
            textAccent="text-slate-900 dark:text-white"
          />
          <Link to="/tutor/earnings" className="block text-left h-full">
            <StatCard
              icon={<Coins className="h-5 w-5" />}
              label="Net Earnings"
              value={formatTutorCurrency(totalEarnings, payoutCurrency)}
              textAccent="text-slate-900 dark:text-white"
            />
          </Link>
        </div>
      )}

      {/* Class requests / Pending invites */}
      {pendingBookings.length > 0 && (
        <section className="space-y-4">
          <h2 className="text-lg md:text-xl font-bold tracking-tight text-slate-800 dark:text-slate-100 flex items-center gap-2">
            <span className="h-2.5 w-2.5 rounded-full bg-amber-500 animate-ping" />
            Class Invitations ({pendingBookings.length})
          </h2>
          <ul className="space-y-3">
            {pendingBookings.map((booking) => (
              <li key={booking.id}>
                <TutorBookingDashboardCard
                  booking={booking}
                  onUpdate={handleBookingUpdate}
                />
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Scheduled Classes & Earnings ledger snapshot */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-stretch">
        {/* Scheduled upcoming classes card */}
        <div className="lg:col-span-2 rounded-lg border border-slate-200 bg-white p-4 pt-3.5 sm:px-5 sm:pb-5 sm:pt-4 shadow-sm dark:border-slate-700 dark:bg-slate-800 flex flex-col justify-between">
          <div className="flex-1 flex flex-col justify-between">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                Scheduled Classes
              </h2>
              {upcomingClasses.length > 2 && (
                <Link
                  to="/tutor/bookings"
                  className="text-xs font-bold text-brand-600 hover:text-brand-700 dark:text-brand-450 dark:hover:text-brand-300"
                >
                  Manage Classes &rarr;
                </Link>
              )}
            </div>
            {upcomingClasses.length === 0 ? (
              <div className="flex-1 flex flex-col justify-center items-center p-8 text-center space-y-2">
                <CalendarDays className="mx-auto h-8 w-8 text-slate-400 dark:text-slate-500" />
                <p className="text-sm font-semibold text-slate-600 dark:text-slate-400">No scheduled upcoming sessions.</p>
                <p className="text-xs text-slate-450 dark:text-slate-500">New bookings by parents will automatically appear here.</p>
              </div>
            ) : (
              <div className="space-y-3 flex-1 flex flex-col justify-between">
                <ul className="space-y-3 flex-1">
                  {upcomingClasses.slice(0, 2).map((booking) => (
                    <li key={booking.id}>
                      <TutorBookingDashboardCard
                        booking={booking}
                        onUpdate={handleBookingUpdate}
                      />
                    </li>
                  ))}
                </ul>
                <div className="pt-3 border-t border-slate-100 dark:border-slate-700 flex justify-end">
                  <Link
                    to="/tutor/bookings"
                    className="text-xs font-bold text-brand-600 hover:text-brand-700 dark:text-brand-450 dark:hover:text-brand-300"
                  >
                    Manage Classes &rarr;
                  </Link>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Profile Card & Earnings Summary */}
        <div className="rounded-lg border border-slate-200 bg-white p-4 pt-3 sm:px-5 sm:pb-5 sm:pt-3.5 shadow-sm dark:border-slate-700 dark:bg-slate-800 flex flex-col justify-between">
          <div className="flex flex-col h-full justify-between">
            <div className="flex items-center gap-3">
              <div className="h-12 w-12 rounded-xl overflow-hidden border border-slate-100 dark:border-slate-800 bg-slate-100 shrink-0 shadow-inner">
                {profile.photoUrl ? (
                  <img src={profile.photoUrl} alt="" className="h-full w-full object-cover" />
                ) : (
                  <div className="h-full w-full bg-gradient-to-br from-indigo-500 to-blue-600 text-white flex items-center justify-center font-bold text-lg">
                    {user?.firstName.charAt(0)}
                  </div>
                )}
              </div>
              <div className="min-w-0">
                <h3 className="text-sm font-extrabold text-slate-800 dark:text-slate-100 truncate">
                  {profile.displayName || `${user?.firstName} ${user?.lastName}`}
                </h3>
                <p className="text-xs text-slate-500 dark:text-slate-400 font-semibold truncate">{user?.email}</p>
              </div>
            </div>

            <div className="space-y-3 pt-4 flex-1 flex flex-col justify-between">
              <div className="space-y-3">
                {/* Profile completion bar */}
                <div className="space-y-1.5">
                  <div className="flex justify-between items-center text-[10px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                    <span>Profile Progress</span>
                    <span>{profile.profileCompletion}%</span>
                  </div>
                  <div className="h-2 rounded-full bg-slate-100 dark:bg-slate-900 overflow-hidden">
                    <div
                      style={{ width: `${profile.profileCompletion}%` }}
                      className="h-full rounded-full bg-brand-600"
                    />
                  </div>
                </div>

                <div className="h-px bg-slate-100 dark:bg-slate-800" />

                <div className="space-y-2 text-xs font-bold text-slate-500 dark:text-slate-400">
                  <div className="flex justify-between">
                    <span className="font-semibold">Hourly Rate:</span>
                    <span className="text-slate-900 dark:text-slate-300">
                      ${profile.hourlyRate.usdMinor / 100}/hr
                    </span>
                  </div>
                  <div className="flex justify-between gap-2">
                    <span className="font-semibold shrink-0">Subjects:</span>
                    <span className="text-slate-900 dark:text-slate-300 truncate max-w-[160px] text-right">
                      {profile.subjects.join(', ') || '—'}
                    </span>
                  </div>
                  <div className="flex justify-between gap-2">
                    <span className="font-semibold shrink-0">Grade Levels:</span>
                    <span className="text-slate-900 dark:text-slate-300 truncate max-w-[160px] text-right">
                      {profile.gradeLevels.join(', ') || '—'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-semibold">Payout Setting:</span>
                    <span className="text-slate-900 dark:text-slate-300 uppercase">{payoutCurrency}</span>
                  </div>
                </div>
              </div>

              {profile.publishStatus === 'published' && (
                <div className="pt-2 mt-4 border-t border-slate-100 dark:border-slate-800">
                  <Link
                    to={`/tutor/public-profile/${profile.id}`}
                    className="w-full inline-flex items-center justify-center gap-1.5 rounded-xl border border-slate-200 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700/50 py-2.5 text-xs font-bold text-slate-700 dark:text-slate-300 transition-colors cursor-pointer"
                  >
                    View Public Profile
                    <ArrowUpRight className="h-4 w-4" />
                  </Link>
                </div>
              )}
            </div>
          </div>


        </div>
      </div>
    </div>
  );
}
